/**
 * «Отчёты» — вкладка редактора, §12.2 ТЗ.
 *
 * До 15.09.2026 отчёты `S1`–`S3` и `S5` были написаны и покрыты тестами, но
 * открыть их было нечем: адреса существовали, экрана не было. Здесь они
 * собраны вместе с отчётами наставника — `S7` (панель управляющего) и `S8`
 * (сводка собственнику).
 *
 * **Какие отчёты видны, решают права** (D181): курсы — `S1`–`S3`, помощник
 * — `S5`, уровень управляющего — `S7`, владелец — `S8`. Спрятанная кнопка
 * защитой не является: сервер отвечает отказом сам.
 *
 * Чисел экран не считает: рубли, доли и тексты приходят готовыми.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Empty, ErrorState, Loading } from '@shell/index';
import { studyApi } from '../api';
import type { MentorPanel, ReportView } from '../api';
import type { EditorRights } from './Editor';

type Code = 'S7' | 'S8' | 'S1' | 'S2' | 'S3' | 'S5';

const TITLES: Record<Code, string> = {
  S7: 'Панель наставника',
  S8: 'Сводка собственнику',
  S1: 'Прогресс по курсам',
  S2: 'Слабые вопросы',
  S3: 'Активность',
  S5: 'Вопросы помощнику',
};

export function Reports({ rights }: { rights: EditorRights }) {
  const available: Code[] = [
    ...(rights.manager ? (['S7'] as Code[]) : []),
    ...(rights.owner ? (['S8'] as Code[]) : []),
    // «Отчёты по обучению» — своя галочка (D235): прогресс, слабые темы,
    // активность и вопросы помощнику открываются ею, а не правкой курсов.
    ...(rights.reports ? (['S1', 'S2', 'S3', 'S5'] as Code[]) : []),
  ];
  const [code, setCode] = useState<Code | null>(available[0] ?? null);

  if (!code) return <Empty title="Отчётов нет" text="Отчёты открываются по правам в разделах." />;

  return (
    <>
      <div className="editor-tabs">
        {available.map((item) => (
          <Button
            key={item}
            small
            variant={code === item ? 'primary' : 'quiet'}
            onClick={() => setCode(item)}
          >
            {TITLES[item]}
          </Button>
        ))}
      </div>
      {code === 'S7' && <PanelReport />}
      {code === 'S8' && <OwnerReport />}
      {code === 'S1' && <TableReport code="S1" load={studyApi.progressReport} />}
      {code === 'S2' && <TableReport code="S2" load={studyApi.weakTopicsReport} />}
      {code === 'S3' && <TableReport code="S3" load={studyApi.activityReport} />}
      {code === 'S5' && <TableReport code="S5" load={studyApi.assistantReport} />}
    </>
  );
}

function PanelReport() {
  const panel = useQuery({
    queryKey: ['study', 'report', 'S7'],
    queryFn: studyApi.mentorPanel,
  });
  if (panel.isLoading) return <Loading />;
  if (panel.error || !panel.data)
    return <ErrorState error={panel.error} onRetry={() => panel.refetch()} />;
  return <PanelView panel={panel.data} />;
}

export function PanelView({ panel }: { panel: MentorPanel }) {
  if (panel.rows.length === 0) {
    return (
      <Empty
        title="Показателей пока нет"
        text="Панель строится по показателям официантов за последние 28 дней. Они приходят от финансового агента каждую ночь."
      />
    );
  }
  return (
    <>
      {panel.issues.length > 0 && (
        <Card title="Общие проблемы команды">
          <ul>
            {panel.issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </Card>
      )}
      {panel.rows.map((row) => (
        <Card key={row.employee_id} title={row.employee}>
          {row.position && <p className="faint">{row.position}</p>}
          <p>
            {row.zone ? (
              <>
                Зона роста: <b>{row.zone}</b> — было {row.before ?? '—'}, стало {row.after ?? '—'}
              </>
            ) : (
              'Отставаний от среднего по должности нет.'
            )}
          </p>
          {row.goal && <p>Цель: {row.goal.text}</p>}
          <p className="faint">
            Тренировок {row.trainings}
            {row.training_score ? ` (средний балл ${row.training_score})` : ''} · микроуроков{' '}
            {row.micro_sent}, отвечено {row.micro_answered} · уроков пройдено {row.lessons_passed}
          </p>
        </Card>
      ))}
      <p className="faint">{panel.note}</p>
    </>
  );
}

function OwnerReport() {
  const summary = useQuery({
    queryKey: ['study', 'report', 'S8'],
    queryFn: studyApi.ownerSummary,
  });
  if (summary.isLoading) return <Loading />;
  if (summary.error || !summary.data)
    return <ErrorState error={summary.error} onRetry={() => summary.refetch()} />;
  return (
    <Card title="За последние 7 дней">
      <p className="faint">Эта же сводка приходит в бот по понедельникам в 10:00.</p>
      <p style={{ whiteSpace: 'pre-wrap' }}>{summary.data.text}</p>
    </Card>
  );
}

function cell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  return String(value);
}

/** Как показывать строку каждого отчёта: одна строка — одна запись. */
function describe(code: Code, row: Record<string, unknown>): string {
  switch (code) {
    case 'S1':
      return `${cell(row.employee)} · ${cell(row.course)}: ${cell(row.passed)} из ${cell(row.total)}, лучший балл ${cell(row.best_score)}`;
    case 'S2':
      return `«${cell(row.question)}» (${cell(row.lesson)}): верно ${Math.round(Number(row.correct_share) * 100)}% из ${cell(row.answers)}`;
    case 'S3':
      return `${new Date(String(row.day)).toLocaleDateString('ru-RU')}: людей ${cell(row.people)}, действий ${cell(row.events)}`;
    default:
      return `${cell(row.topic)}: ${cell(row.answers)}`;
  }
}

function TableReport({ code, load }: { code: Code; load: () => Promise<ReportView> }) {
  const report = useQuery({
    queryKey: ['study', 'report', code],
    queryFn: load,
  });
  if (report.isLoading) return <Loading />;
  if (report.error || !report.data)
    return <ErrorState error={report.error} onRetry={() => report.refetch()} />;
  const { rows, note, title } = report.data;
  return (
    <Card title={title}>
      {rows.length === 0 ? (
        <p className="faint">Пока пусто.</p>
      ) : (
        <ul>
          {rows.map((row, index) => (
            <li key={index}>
              {describe(code, row)}
              {Array.isArray(row.questions) && row.questions.length > 0 && (
                <ul>
                  {(row.questions as string[]).map((question) => (
                    <li key={question}>{question}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      {note && <p className="faint">{note}</p>}
    </Card>
  );
}
